import * as React from 'react'
import { Search, PhoneCall, RefreshCw, Pencil, MessageSquareText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { AgentDocumentsSelector } from '@/components/agents/AgentDocumentsSelector'
import { useDirectories } from '@/hooks/use-directories'
import { cn } from '@/lib/utils'
import { log } from '@/lib/logger'
import { ELEVENLABS_MAX_FILE_BYTES } from '@/config/env'
import {
  agentsService,
} from '@/services/agentsService'
import {
  elevenlabsAgentsService,
  type ElevenLabsAgent,
  type ElevenLabsAgentKnowledgeBase,
  type ElevenLabsAgentsResponse,
} from '@/services/elevenlabsAgentsService'

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'Ha ocurrido un error inesperado.'
}

function formatDateFromUnix(value?: number): string {
  if (!value) return '-'
  const date = new Date(value * 1000)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  })
}

function formatBytesToMB(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB'
  const mb = bytes / (1024 * 1024)
  return `${mb.toFixed(0)} MB`
}

export function CallAgentsPage() {
  const { t } = useTranslation()
  const [agents, setAgents] = React.useState<ElevenLabsAgent[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [selectedAgent, setSelectedAgent] = React.useState<ElevenLabsAgent | null>(null)
  const [selectedDocumentIds, setSelectedDocumentIds] = React.useState<number[]>([])
  const [saving, setSaving] = React.useState(false)
  const [bulkSelectingFolder, setBulkSelectingFolder] = React.useState(false)
  const [kbLoading, setKbLoading] = React.useState(false)
  const [kbInfo, setKbInfo] = React.useState<ElevenLabsAgentKnowledgeBase | null>(null)
  const [constraints, setConstraints] = React.useState<ElevenLabsAgentsResponse['constraints'] | null>(null)
  const [selectedDirectoryId, setSelectedDirectoryId] = React.useState<string>('all')
  const { flatDirectories } = useDirectories()
  const [promptDialogOpen, setPromptDialogOpen] = React.useState(false)
  const [promptAgent, setPromptAgent] = React.useState<ElevenLabsAgent | null>(null)
  const [promptText, setPromptText] = React.useState('')
  const [firstMessage, setFirstMessage] = React.useState('')
  const [promptLoading, setPromptLoading] = React.useState(false)
  const [promptSaving, setPromptSaving] = React.useState(false)

  const maxFileBytes = constraints?.max_file_bytes ?? ELEVENLABS_MAX_FILE_BYTES
  const allowedDocumentTypes = constraints?.allowed_local_document_types || ['pdf', 'txt', 'doc', 'url']

  const allowedFormatsLabel = React.useMemo(() => {
    const exts = constraints?.allowed_upload_extensions || []
    if (exts.length === 0) {
      return '.pdf, .txt, .doc, .docx, URL'
    }
    const sorted = [...exts].sort((a, b) => a.localeCompare(b))
    return `${sorted.join(', ')}, URL`
  }, [constraints])

  const loadAgents = React.useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await elevenlabsAgentsService.listAgents()
      setAgents(data.items || [])
      setConstraints(data.constraints || null)
    } catch (loadError) {
      log.error('CallAgentsPage loadAgents failed', loadError)
      setError(toErrorMessage(loadError))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadAgents()
  }, [loadAgents])

  const filteredAgents = React.useMemo(() => {
    if (!searchQuery.trim()) return agents
    const query = searchQuery.toLowerCase()
    return agents.filter((agent) => {
      return agent.name?.toLowerCase().includes(query) || agent.agent_id?.toLowerCase().includes(query)
    })
  }, [agents, searchQuery])

  const openAssignDialog = (agent: ElevenLabsAgent) => {
    setSelectedAgent(agent)
    setSelectedDocumentIds([])
    setSelectedDirectoryId('all')
    setDialogOpen(true)
  }

  const openPromptDialog = (agent: ElevenLabsAgent) => {
    setPromptAgent(agent)
    setPromptText('')
    setFirstMessage('')
    setPromptDialogOpen(true)
  }

  React.useEffect(() => {
    if (!dialogOpen || !selectedAgent) return
    let active = true
    const loadKnowledgeBase = async () => {
      try {
        setKbLoading(true)
        const info = await elevenlabsAgentsService.getAgentKnowledgeBase(selectedAgent.agent_id)
        if (!active) return
        setKbInfo(info)
        setSelectedDocumentIds(info.local_document_ids || [])
      } catch (kbError) {
        if (!active) return
        log.error('CallAgentsPage loadKnowledgeBase failed', kbError)
        toast.error(toErrorMessage(kbError))
      } finally {
        if (active) setKbLoading(false)
      }
    }
    void loadKnowledgeBase()
    return () => {
      active = false
    }
  }, [dialogOpen, selectedAgent])

  React.useEffect(() => {
    if (!promptDialogOpen || !promptAgent) return
    let active = true
    const loadPromptConfig = async () => {
      try {
        setPromptLoading(true)
        const info = await elevenlabsAgentsService.getAgentPromptConfig(promptAgent.agent_id)
        if (!active) return
        setPromptText(info.prompt || '')
        setFirstMessage(info.first_message || '')
      } catch (promptError) {
        if (!active) return
        log.error('CallAgentsPage loadPromptConfig failed', promptError)
        toast.error(toErrorMessage(promptError))
      } finally {
        if (active) setPromptLoading(false)
      }
    }
    void loadPromptConfig()
    return () => {
      active = false
    }
  }, [promptDialogOpen, promptAgent])

  const handleAssignDocuments = async () => {
    if (!selectedAgent) return
    const lastKnownRemoteSelection = kbInfo?.local_document_ids || []
    const selectedSet = new Set(selectedDocumentIds)
    const removableRemoteIds = (kbInfo?.local_documents || [])
      .filter((doc) => !selectedSet.has(doc.id))
      .map((doc) => doc.remote_document_id)
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    const uniqueRemovals = Array.from(new Set(removableRemoteIds))

    if (selectedDocumentIds.length === 0 && uniqueRemovals.length === 0) {
      toast.warning(t('agent_studio.call_agents_modal_select_one'))
      return
    }
    try {
      setSaving(true)
      if (selectedDocumentIds.length > 0) {
        await elevenlabsAgentsService.syncAndAssignLocalDocuments(selectedAgent.agent_id, selectedDocumentIds)
      }
      if (uniqueRemovals.length > 0) {
        await elevenlabsAgentsService.unassignDocuments(selectedAgent.agent_id, uniqueRemovals)
      }
      toast.success(t('agent_studio.call_agents_modal_success'))
      const info = await elevenlabsAgentsService.getAgentKnowledgeBase(selectedAgent.agent_id)
      setKbInfo(info)
      setSelectedDocumentIds(info.local_document_ids || [])
      setDialogOpen(false)
    } catch (assignError) {
      log.error('CallAgentsPage handleAssignDocuments failed', assignError)
      toast.error(toErrorMessage(assignError))
      // Keep UI aligned with remote truth when assign/unassign fails.
      try {
        const info = await elevenlabsAgentsService.getAgentKnowledgeBase(selectedAgent.agent_id)
        setKbInfo(info)
        setSelectedDocumentIds(info.local_document_ids || [])
      } catch (refreshError) {
        log.error('CallAgentsPage handleAssignDocuments refresh failed', refreshError)
        setSelectedDocumentIds(lastKnownRemoteSelection)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleSelectFolderDocuments = async () => {
    if (selectedDirectoryId === 'all') {
      toast.warning(t('agent_studio.call_agents_modal_folder_select_required'))
      return
    }

    const directoryId = Number(selectedDirectoryId)
    if (!Number.isFinite(directoryId) || directoryId <= 0) {
      toast.warning(t('agent_studio.call_agents_modal_folder_select_required'))
      return
    }

    try {
      setBulkSelectingFolder(true)
      const docs = await agentsService.listAllAvailableDocuments({
        status: 'embedded',
        directoryId,
        documentTypes: allowedDocumentTypes,
      })
      const ids = docs.map((item) => item.id)
      setSelectedDocumentIds((prev) => Array.from(new Set([...prev, ...ids])))
      toast.success(t('agent_studio.call_agents_modal_folder_selected_count', { count: ids.length }))
    } catch (error) {
      log.error('CallAgentsPage handleSelectFolderDocuments failed', error)
      toast.error(toErrorMessage(error))
    } finally {
      setBulkSelectingFolder(false)
    }
  }

  const handleSavePrompt = async () => {
    if (!promptAgent) return
    try {
      setPromptSaving(true)
      await elevenlabsAgentsService.updateAgentPromptConfig(promptAgent.agent_id, {
        prompt: promptText,
        first_message: firstMessage,
      })
      toast.success(t('agent_studio.call_agents_prompt_success'))
      setPromptDialogOpen(false)
    } catch (promptError) {
      log.error('CallAgentsPage handleSavePrompt failed', promptError)
      toast.error(toErrorMessage(promptError))
    } finally {
      setPromptSaving(false)
    }
  }

  return (
    <div className="container mx-auto space-y-4 p-6">
      <section>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <PhoneCall className="h-4 w-4" />
              {t('agent_studio.call_agents_header')}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {t('agent_studio.call_agents_title')}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('agent_studio.call_agents_description', { limit: formatBytesToMB(maxFileBytes) })}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('agent_studio.call_agents_allowed_formats', { formats: allowedFormatsLabel })}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="h-9 px-3 text-xs text-muted-foreground">
              {t('agent_studio.call_agents_limit', { limit: formatBytesToMB(maxFileBytes) })}
            </Badge>
            <Button size="default" variant="secondary" onClick={() => void loadAgents()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('agent_studio.call_agents_refresh')}
            </Button>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('agent_studio.call_agents_search')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="pl-9"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            {t('agent_studio.call_agents_readonly')}
          </div>
        </div>
      </section>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive break-words overflow-hidden">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {loading && Array.from({ length: 6 }).map((_, index) => (
          <Card key={`skeleton-${index}`} className="border-border">
            <CardContent className="space-y-2 p-4">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
        ))}

        {!loading && filteredAgents.length === 0 && (
          <div className="col-span-full rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {t('agent_studio.call_agents_empty')}
          </div>
        )}

        {!loading && filteredAgents.map((agent) => (
          <Card key={agent.agent_id} className="border-border bg-card/70">
            <CardContent className="space-y-4 p-5">
              <div className="flex items-start gap-2">
                <div>
                  <p className="text-lg font-semibold text-foreground">
                    {agent.name || t('agent_studio.call_agents_unnamed')}
                  </p>
                  <p className="text-xs text-muted-foreground">ID: {agent.agent_id}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant="outline"
                  className={cn('border-border text-xs text-muted-foreground')}
                >
                  Creado: {formatDateFromUnix(agent.created_at_unix_secs)}
                </Badge>
                {(agent.tags || []).slice(0, 3).map((tag) => (
                  <Badge key={`${agent.agent_id}-${tag}`} variant="secondary">
                    {tag}
                  </Badge>
                ))}
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">{t('agent_studio.call_agents_assignable')}</span>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => openPromptDialog(agent)}>
                    <MessageSquareText className="mr-2 h-4 w-4" />
                    {t('agent_studio.call_agents_prompt_edit')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openAssignDialog(agent)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    {t('agent_studio.call_agents_edit')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('agent_studio.call_agents_modal_title')}</DialogTitle>
            <DialogDescription>
              {t('agent_studio.call_agents_modal_description')}
              <span className="block text-xs text-muted-foreground">
                {t('agent_studio.call_agents_description', { limit: formatBytesToMB(maxFileBytes) })}
              </span>
              <span className="block text-xs text-muted-foreground">
                {t('agent_studio.call_agents_allowed_formats', { formats: allowedFormatsLabel })}
              </span>
            </DialogDescription>
          </DialogHeader>

          {selectedAgent && (
            <div className="space-y-4">
              <div className="text-sm">
                <span className="font-medium">{t('agent_studio.call_agents_modal_agent')}:</span> {selectedAgent.name || selectedAgent.agent_id}
              </div>
              <AgentDocumentsSelector
                selectedDocumentIds={selectedDocumentIds}
                onChange={setSelectedDocumentIds}
                listHeightClass="h-[45vh]"
                directoryId={selectedDirectoryId === 'all' ? null : Number(selectedDirectoryId)}
                documentTypes={allowedDocumentTypes}
                labels={{
                  selected: t('agent_studio.call_agents_modal_selected'),
                  searchPlaceholder: t('agent_studio.call_agents_modal_search'),
                  loading: t('agent_studio.call_agents_modal_loading'),
                  empty: t('agent_studio.call_agents_modal_empty'),
                  category: t('agent_studio.call_agents_modal_category'),
                  subcategory: t('agent_studio.call_agents_modal_subcategory'),
                }}
              />
              <div className="grid gap-2 md:grid-cols-[1fr_auto]">
                <Select value={selectedDirectoryId} onValueChange={setSelectedDirectoryId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('agent_studio.call_agents_modal_folder_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('agent_studio.call_agents_modal_folder_all')}</SelectItem>
                    {flatDirectories.map((dir) => (
                      <SelectItem key={dir.id} value={dir.id}>
                        {dir.path}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSelectFolderDocuments}
                  disabled={bulkSelectingFolder}
                >
                  {bulkSelectingFolder
                    ? t('agent_studio.call_agents_modal_folder_selecting')
                    : t('agent_studio.call_agents_modal_folder_select')}
                </Button>
              </div>
              {kbLoading && (
                <div className="text-xs text-muted-foreground">{t('agent_studio.call_agents_modal_syncing')}</div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={handleAssignDocuments} disabled={saving}>
              {saving ? t('agent_studio.call_agents_modal_assigning') : t('agent_studio.call_agents_modal_assign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={promptDialogOpen} onOpenChange={setPromptDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('agent_studio.call_agents_prompt_title')}</DialogTitle>
            <DialogDescription>{t('agent_studio.call_agents_prompt_description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm">
              <span className="font-medium">{t('agent_studio.call_agents_modal_agent')}:</span>{' '}
              {promptAgent?.name || promptAgent?.agent_id}
            </div>
            <div className="space-y-2">
              <Label htmlFor="call-agent-first-message">
                {t('agent_studio.call_agents_prompt_first_message')}
              </Label>
              <Textarea
                id="call-agent-first-message"
                value={firstMessage}
                onChange={(event) => setFirstMessage(event.target.value)}
                rows={3}
                disabled={promptLoading || promptSaving}
                placeholder={t('agent_studio.call_agents_prompt_first_message_placeholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="call-agent-prompt">{t('agent_studio.call_agents_prompt_label')}</Label>
              <Textarea
                id="call-agent-prompt"
                value={promptText}
                onChange={(event) => setPromptText(event.target.value)}
                rows={10}
                disabled={promptLoading || promptSaving}
                placeholder={t('agent_studio.call_agents_prompt_placeholder')}
                className="font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSavePrompt} disabled={promptLoading || promptSaving}>
              {promptSaving
                ? t('agent_studio.call_agents_prompt_saving')
                : t('agent_studio.call_agents_prompt_save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
